import React from 'react';
import {
  FunnelIcon,
  Cog6ToothIcon,
  ChevronDoubleRightIcon,
  ChevronDoubleLeftIcon,
} from '@heroicons/react/24/outline';
import { useLayout } from '@/contexts';
import VblForecastSettingsSidebar, { VblForecastCISettings } from './VblForecastSettingsSidebar';
import VblForecastFilterSidebar from './VblForecastFilterSidebar';
import { VblForecastFilters } from '@/types/vblForecast';

interface VblForecastSidebarControllerProps {
  settings: VblForecastCISettings;
  onSettingsChange: (settings: VblForecastCISettings) => void;
  filters: VblForecastFilters;
  onFiltersChange: (filters: VblForecastFilters) => void;
  availableOptions: {
    sites: string[];
    turbineModels: string[];
    componentsMajor: string[];
    componentsMinor: string[];
  };
  onApply: () => void;
}

export const VblForecastSidebarController: React.FC<VblForecastSidebarControllerProps> = ({
  settings,
  onSettingsChange,
  filters,
  onFiltersChange,
  availableOptions,
  onApply,
}) => {
  const { rightSidebarType, setRightSidebarType } = useLayout();

  // Toggle individual sidebars
  const toggleSidebar = (type: 'filters' | 'analysis') => {
    const current = rightSidebarType;
    if (current === 'none') {
      setRightSidebarType(type);
    } else if (current === type) {
      setRightSidebarType('none');
    } else if (current === 'both') {
      setRightSidebarType(type === 'filters' ? 'analysis' : 'filters');
    } else {
      setRightSidebarType('both');
    }
  };

  // Toggle all sidebars
  const toggleAll = () => {
    const current = rightSidebarType;
    if (current === 'both') {
      setRightSidebarType('none');
    } else {
      setRightSidebarType('both');
    }
  };

  // Determine if specific sidebar is active
  const isFilterSidebarActive = rightSidebarType === 'filters' || rightSidebarType === 'both';
  const isAnalysisSidebarActive = rightSidebarType === 'analysis' || rightSidebarType === 'both';

  // Calculate active filter count for badge
  const activeFilterCount = Object.values(filters).reduce((count, filterArray) => {
    return count + (Array.isArray(filterArray) ? filterArray.length : 0);
  }, 0);

  return (
    <div className="flex h-full">
      {/* Vertically Stacked Sidebars */}
      <div className="flex flex-col h-full">
        {/* Filter Sidebar */}
        {isFilterSidebarActive && (
          <div className={`${isAnalysisSidebarActive ? 'h-1/2' : 'h-full'}`}>
            <VblForecastFilterSidebar
              filters={filters}
              onFiltersChange={onFiltersChange}
              availableOptions={availableOptions}
              isCollapsed={false}
              onToggleCollapse={() => toggleSidebar('filters')}
            />
          </div>
        )}

        {/* Settings Sidebar */}
        {isAnalysisSidebarActive && (
          <div className={isFilterSidebarActive ? "h-1/2" : "h-full"}>
            <VblForecastSettingsSidebar
              settings={settings}
              onSettingsChange={onSettingsChange}
              onApply={onApply}
              isCollapsed={false}
              onToggleCollapse={() => toggleSidebar('analysis')}
            />
          </div>
        )}
      </div>

      {/* Icon Strip - Always visible on the right */}
      <div className="bg-white h-full flex flex-col w-12 relative border-l border-gray-200">
        {/* Main Toggle Button */}
        <div className="p-2">
          <button
            onClick={toggleAll}
            className="p-2 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            title={rightSidebarType === 'both' ? 'Close all sidebars' : 'Open all sidebars'}
          >
            {rightSidebarType === 'both' ? (
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            ) : (
              <ChevronDoubleRightIcon className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Individual Sidebar Icons */}
        <div className="flex flex-col space-y-2 p-2">
          {/* Filter Icon */}
          <button
            onClick={() => toggleSidebar('filters')}
            className={`p-2 rounded-md transition-colors relative ${isFilterSidebarActive
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
              }`}
            title={isFilterSidebarActive ? 'Close VBL forecast filters' : 'Open VBL forecast filters'}
          >
            <FunnelIcon className="h-5 w-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Settings Icon */}
          <button
            onClick={() => toggleSidebar('analysis')}
            className={`p-2 rounded-md transition-colors ${isAnalysisSidebarActive
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
              }`}
            title={isAnalysisSidebarActive ? 'Close VBL forecast settings' : 'Open VBL forecast settings'}
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VblForecastSidebarController;